/**
 * Módulo del tutorial: Pesajes y GDP (KAN-232).
 *
 * Práctica real con DOS sesiones de pesaje separadas por un mes simulado:
 * sesión 1 (mes pasado): ternero de prueba 999 con 100 kg y fecha de hace un
 * mes → se cierra con Salir. Sesión 2 (hoy): nueva sesión, misma caravana,
 * 130 kg con fecha de hoy — con eso la app calcula la GDP real. Después se
 * activan y explican las columnas calculadas de la lista (Pesajes, Peso Est.,
 * Ganancia, Días s/Pes., Engorde), se abre la ficha por el ojito (Progreso de
 * engorde → Evolución de Peso) y se cierra con la meta en Configuraciones →
 * Peso Objetivo.
 *
 * Anclas: menu-animals, animals-btn-nueva-sesion, field-selector-start,
 * form-animal-caravana/categoria/peso/fecha-evento, btn-save-animal,
 * animal-session-salir, animals-btn-columnas, col-calc-calc_* (checkboxes),
 * animals-row-ver (ojito), animal-gdp-card, animal-btn-evolucion,
 * config-tab-pesoObjetivo.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { sesionAbiertaConAnimales } from '../../../services/tutorial/sessionCondition';
import { getRepositories } from '../../../services/storage';

export const pesajesModule: TutorialModuleDef = {
  id: 'pesajes',
  title: 'Pesajes y ganancia diaria',
  description: 'Practicá cargando dos sesiones de pesaje a un mes de distancia y aprendé a leer la ganancia diaria: las columnas, la ficha y tu meta de engorde.',
  emoji: '📈',
  image: 'images/tutorial/pesajes.png',
  permission: { module: 'animals', action: 'create' },
  grupo: 'esencial',
  route: '/animals',
  steps: [
    {
      id: 'concepto-pesajes',
      route: '/tutorial',
      text: 'El peso es el pulso del animal: con dos pesajes, la app calcula la GDP — cuánto engorda por día. Vamos a demostrarlo cargando DOS sesiones de pesaje, como dos mangueadas con un mes de por medio.',
      narration:
        'El peso es el pulso de tu hacienda. Un peso suelto dice poco; dos, dicen todo: con esa resta, la app calcula la ganancia diaria, cuántos kilos engorda cada animal por día. Y para que lo veas con tus propios ojos, vamos a hacer la demostración completa: cargamos dos sesiones de pesaje, como dos mangueadas con un mes de por medio, y después aprendemos a leer el resultado en todas partes.',
    },
    {
      id: 'ir-a-mi-ganado',
      target: '[data-ar-guide="menu-animals"]',
      label: 'Mi Ganado (menú Ganado)',
      text: 'Empecemos en "Mi Ganado" (menú Ganado): esta página es la lista de TODOS tus animales — acá va a vivir el ternero de práctica.',
      narration:
        'Arrancamos en casa: Mi Ganado, en el menú de la izquierda, sección Ganado. Esta página es la lista de todos tus animales, con su caravana, su categoría y su peso. Acá vamos a hacer la práctica de punta a punta: cargar dos pesajes de un ternero y aprender a leer lo que la app saca de ellos.',
    },
    {
      id: 'nueva-sesion-pesaje',
      route: '/animals',
      target: '[data-ar-guide="animals-btn-nueva-sesion"]',
      label: 'Nueva Sesión (la 1ª de dos)',
      text: 'Tocá "Nueva Sesión": vamos a crear DOS sesiones de pesaje para la demostración — ésta va a ser la del "mes pasado".',
      narration:
        'Como todo en esta app, los pesajes viven en sesiones. Y hoy vamos con dos: ésta primera representa la mangueada del mes pasado, y después abriremos otra como la de hoy. Tocá Nueva Sesión y arrancamos.',
    },
    {
      // Paso condicional: sólo aparece si el usuario ya tenía una sesión de
      // carga abierta con animales (las sesiones quedan abiertas hasta que
      // se inicia una nueva — SessionContext persiste en localStorage).
      id: 'sesion-abierta-pesaje',
      route: '/animals/new',
      target: '[data-ar-guide="sesion-modal-nueva"]',
      label: 'Cerrá la sesión anterior',
      showIf: sesionAbiertaConAnimales,
      text: '¡Ojo! Tenés una sesión ABIERTA de la última vez (las sesiones quedan abiertas hasta que iniciás una nueva). Tocá "Nueva Sesión" acá para cerrar esa y arrancar la del "mes pasado".',
      narration:
        'Alto el caballo: tenés una sesión abierta de la última vez. No se perdió nada, es que las sesiones de esta app quedan abiertas hasta que vos iniciás una nueva, aunque cierre el navegador o pasen días. Tocá Nueva Sesión, el botón colorado de este aviso: la vieja queda guardada con su fecha, y arrancamos la del mes pasado para la práctica.',
    },
    {
      id: 'selector-peso',
      route: '/animals/new',
      target: '[data-ar-guide="field-selector-start"]',
      label: 'Elegí los datos (Peso incluido)',
      text: 'Marcá "Peso" entre los datos a cargar (o usá "Carga rápida", que ya lo trae) y tocá "Comenzar a cargar".',
      narration:
        'Si te aparece esta pantalla, marcá que querés cargar el Peso, que es el protagonista de hoy. Si preferís, Carga rápida ya lo trae marcado, junto con lo esencial. Cuando estés, Comenzar a cargar.',
    },
    {
      id: 'ternero-caravana',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-caravana"]',
      label: 'Caravana de prueba: 999',
      advanceOnClick: false,
      text: 'Vamos a practicar con un ternero de prueba: cargá la caravana 999. Total, después lo podés borrar.',
      narration:
        'La práctica va con un ternero, que es donde el peso más importa: de la guacha al destete, cada kilo es negocio. Cargá la caravana nueve nueve nueve, un animal de prueba: cuando termines el módulo lo borrás y chau, sin dejar rastro.',
    },
    {
      id: 'ternero-categoria',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-categoria"]',
      label: 'Categoría: Ternero',
      advanceOnClick: false,
      text: 'En categoría elegí "Ternero". Fijate que al elegirlo, el campo Peso se vuelve obligatorio: en los guachos el kilaje es la clave.',
      narration:
        'Ahora la categoría: Ternero. ¿Ves que al elegirla el campo de peso se vuelve obligatorio? No es capricho: en los guachos el kilaje es justamente lo que hay que seguir, marca a marca, para saber si la cría viene bien.',
    },
    {
      id: 'primer-peso',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-peso"]',
      label: 'Primer pesaje: 100 kg',
      advanceOnClick: false,
      text: 'Para este test, anotá 100 kg: ése es el peso del primer pesaje del 999.',
      narration:
        'Vamos con el primer pesaje. Para que la demostración dé números redondos, anotá cien kilos: ése es el punto de partida del ternero. La marca de donde parte toda la cuenta.',
    },
    {
      id: 'fecha-evento-1',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-fecha-evento"]',
      label: 'Fecha de Evento: hace un mes',
      advanceOnClick: false,
      text: 'Para este caso, supongamos que el primer pesaje fue HACE UN MES: poné en "Fecha de Evento" la fecha de un mes atrás. La app usa ESA fecha para contar los días.',
      narration:
        'Y ahora un campo que nadie mira y es la clave de todo: Fecha de Evento. Esta sesión representa la mangueada del mes pasado, así que poné la fecha de hace un mes, no la de hoy. La app cuenta los días entre fechas para sacar la ganancia por día: si la fecha miente, el número miente.',
    },
    {
      id: 'guardar-peso-1',
      route: '/animals/new',
      target: '[data-ar-guide="btn-save-animal"]',
      label: 'Guardar 1er pesaje',
      text: 'Guardá: el primer pesaje del 999 quedó registrado con su fecha de hace un mes.',
      narration:
        'Apretá guardar y quedó marcado el primer pesaje del ternero: cien kilos, fechados hace un mes. Ahora cerramos esta jornada para simular que pasó el tiempo.',
    },
    {
      id: 'cerrar-sesion-1',
      route: '/animals/new',
      target: '[data-ar-guide="animal-session-salir"]',
      label: 'Salir (cerramos la sesión del "mes pasado")',
      text: 'Asumimos que esa sesión fue el mes pasado: tocá "Salir" y la cerramos.',
      narration:
        'Cerramos esta primera sesión, la del mes pasado: tocá Salir, ahí en la barra de la sesión, y la jornada queda guardada con su fecha. Aprendé de paso cómo funcionan las sesiones en esta app: quedan abiertas hasta que vos iniciás una nueva, aunque cierres el navegador o pasen días. Por eso conviene cerrarlas o arrancar una limpia con cada mangueada. Así se trabaja en la vida real: cada jornada, su sesión.',
    },
    {
      id: 'nueva-sesion-2',
      route: '/animals',
      target: '[data-ar-guide="animals-btn-nueva-sesion"]',
      label: 'Nueva Sesión (la de hoy)',
      text: 'Supongamos que pasó un mes desde esa sesión donde cargamos el 999: iniciamos una NUEVA sesión de pesajes para cargarle el segundo pesaje.',
      narration:
        '¿Ya pasaron unas cuantas lunas? Bien: simulemos que pasó un mes desde esa sesión donde cargamos nuestro ternero. Tocá Nueva Sesión otra vez: una jornada nueva, como la mangueada de hoy, para cargarle el segundo pesaje a los animales, el 999 entre ellos.',
    },
    {
      // Paso condicional de la 2ª entrada: normalmente no aparece (el módulo
      // cerró la sesión 1 con Salir), pero cubre al que llegó con una sesión
      // abierta de antes — el modal bloquea el selector de campos.
      id: 'sesion-abierta-pesaje-2',
      route: '/animals/new',
      target: '[data-ar-guide="sesion-modal-nueva"]',
      label: 'Cerrá la sesión anterior',
      showIf: sesionAbiertaConAnimales,
      text: '¡Ojo! Tenés una sesión ABIERTA (las sesiones quedan abiertas hasta que iniciás una nueva). Tocá "Nueva Sesión" acá para cerrar esa y arrancar la de "hoy".',
      narration:
        'Aviso: tenés una sesión abierta de antes, y el selector no aparece hasta resolverla. Tocá Nueva Sesión, el botón colorado de este aviso: la anterior queda guardada, y arrancamos la de hoy para el segundo pesaje.',
    },
    {
      id: 'selector-peso-2',
      route: '/animals/new',
      target: '[data-ar-guide="field-selector-start"]',
      label: 'Comenzar a cargar (otra vez)',
      text: 'Como la vez pasada: marcá "Peso" (o "Carga rápida") y "Comenzar a cargar".',
      narration:
        'Otra vez el arranque de sesión, ya lo conocés: marcá el Peso entre los datos, o Carga rápida si querés ir al bocho, y Comenzar a cargar.',
    },
    {
      id: 'caravana-999-otra-vez',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-caravana"]',
      label: 'Misma caravana: 999',
      advanceOnClick: false,
      text: 'Cargá OTRA VEZ la caravana 999: la app la reconoce y le suma este segundo pesaje a su historia.',
      narration:
        'Segundo pesaje, mismo bicho: volvé a cargar la caravana nueve nueve nueve. La app la reconoce al toque y le suma este evento a su historia; el pesaje nuevo cae junto al de hace un mes. Así se arma el historial, pesaje a pesaje, sesión a sesión.',
    },
    {
      id: 'peso-segundo',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-peso"]',
      label: 'Segundo pesaje: 130 kg',
      advanceOnClick: false,
      text: 'El segundo pesaje es HOY: anotá 130 kg — 30 kilos más que el mes pasado.',
      narration:
        'Ahora el kilaje de hoy: anotá ciento treinta kilos. Treinta más que hace un mes, un guacho bien alimentado a razón de un kilo por día. ¿Y sabés qué va a pasar cuando guardes? Con esos dos pesos y sus dos fechas, la app le saca la ganancia diaria sola.',
    },
    {
      id: 'fecha-evento-2',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-fecha-evento"]',
      label: 'Fecha de Evento: hoy',
      advanceOnClick: false,
      text: 'La fecha de este pesaje es HOY (un mes después del primero): dejá la fecha de hoy que ya viene.',
      narration:
        'Y la Fecha de Evento de este segundo pesaje es hoy, un mes justo después del primero: dejá la fecha como viene, que ya trae el día de hoy. Dos pesos, dos fechas, un mes de por medio: ésa es toda la materia prima que necesita la app.',
    },
    {
      id: 'guardar-peso-2',
      route: '/animals/new',
      target: '[data-ar-guide="btn-save-animal"]',
      label: 'Guardar 2º pesaje',
      text: 'Guardá el segundo pesaje. ¡Listo! El 999 ya tiene su GDP calculada.',
      narration:
        'Guardá, y listo: tu ternero ya tiene su ganancia diaria calculada, un kilo por día de pura verdad. Ahora te enseño a leerla en todas partes.',
    },
    {
      id: 'cerrar-sesion-2',
      route: '/animals/new',
      target: '[data-ar-guide="animal-session-salir"]',
      label: 'Salir (cerrar la sesión de hoy)',
      text: 'Cerrá también esta segunda sesión con "Salir" y volvés a Mi Ganado.',
      narration:
        'Cerremos también la jornada de hoy con Salir, y de vuelta a Mi Ganado a mirar lo que se armó con nuestros dos pesajes.',
    },
    {
      id: 'abrir-columnas',
      route: '/animals',
      target: '[data-ar-guide="animals-btn-columnas"]',
      label: 'Botón Columnas',
      text: 'De vuelta en Mi Ganado, tocá "Columnas": vas a activar las columnas que muestran el engorde de cada animal.',
      narration:
        'Ya de vuelta en la lista, buscate el botón Columnas, al lado del botón de Filtros. Ahí elegís qué columnas ves, y hay unas cuantas escondidas que son puro oro para el engorde. Vamos a prenderlas una por una, y te cuento qué dice cada una.',
    },
    {
      id: 'col-pesajes',
      route: '/animals',
      target: '[data-ar-guide="col-calc-calc_pesajes"]',
      extraTargets: ['[data-ar-guide="col-th-calc_pesajes"]'],
      label: '🧮 Pesajes',
      text: 'Tildá "🧮 Pesajes": te dice CUÁNTOS pesajes tiene registrados cada animal. El 999 tiene que marcar 2.',
      narration:
        'La primera: Pesajes. Te dice cuántos pesajes tiene registrados el animal. Mirá el nueve nueve nueve: tiene que estar marcando dos, uno por cada sesión. La vas a usar para saber a quién le falta un pesaje.',
    },
    {
      id: 'col-peso-estimado',
      route: '/animals',
      target: '[data-ar-guide="col-calc-calc_peso_estimado"]',
      extraTargets: ['[data-ar-guide="col-th-calc_pesajes"]', '[data-ar-guide="col-th-calc_peso_estimado"]'],
      label: '🧮 Peso Est.',
      text: '"Peso Est." es el peso de HOY estimado: último pesaje + ganancia diaria × días pasados. Azul cuando la app estimó para arriba.',
      narration:
        'La segunda: Peso Estimado. Es el peso de hoy, calculado: el último pesaje, más la ganancia diaria por los días que pasaron. Así sabés cuánto pesa el animal hoy sin pasarlo por la manga. Cuando sale en azul, es que la app estimó para arriba del último pesaje.',
    },
    {
      id: 'col-ganancia',
      route: '/animals',
      target: '[data-ar-guide="col-calc-calc_ganancia_total"]',
      extraTargets: [
        '[data-ar-guide="col-th-calc_pesajes"]',
        '[data-ar-guide="col-th-calc_peso_estimado"]',
        '[data-ar-guide="col-th-calc_ganancia_total"]',
      ],
      label: '🧮 Ganancia',
      text: '"Ganancia": los kilos TOTALES desde el primer pesaje. Verde si sumó, rojo si adelgazó — de un vistazo, quién progresa. El 999: +30.',
      narration:
        'La tercera: Ganancia. Es la resta simple y honesta: el peso de hoy menos el del primer pesaje. Los kilos totales que sumó el animal desde que lo empezaste a pesar. Verde si engordó, rojo si adelgazó. Nuestro ternero tiene que estar marcando más treinta, verdecito.',
    },
    {
      id: 'col-dias-pesaje',
      route: '/animals',
      target: '[data-ar-guide="col-calc-calc_dias_pesaje"]',
      extraTargets: [
        '[data-ar-guide="col-th-calc_pesajes"]',
        '[data-ar-guide="col-th-calc_peso_estimado"]',
        '[data-ar-guide="col-th-calc_ganancia_total"]',
        '[data-ar-guide="col-th-calc_dias_pesaje"]',
      ],
      label: '🧮 Días s/Pes.',
      text: '"Días s/Pes." = días SIN pesaje desde el último. Amarillo después de 60 días, rojo después de 90: tu recordatorio de volver a la manga.',
      narration:
        'La cuarta, y ojo que ésta es la que más se agradece: Días sin Pesaje. Te dice hace cuántos días no pesás a cada animal. Se pone amarilla pasados los sesenta días, y roja pasados los noventa: es tu propia app recordándote que hay que volver a la manga. El pesaje viejo no sirve de mucho.',
    },
    {
      id: 'col-engorde',
      route: '/animals',
      target: '[data-ar-guide="col-calc-calc_engorde"]',
      extraTargets: [
        '[data-ar-guide="col-th-calc_pesajes"]',
        '[data-ar-guide="col-th-calc_peso_estimado"]',
        '[data-ar-guide="col-th-calc_ganancia_total"]',
        '[data-ar-guide="col-th-calc_dias_pesaje"]',
        '[data-ar-guide="col-th-calc_engorde"]',
      ],
      label: '🧮 Engorde',
      text: '"Engorde": la barra que muestra cuánto le falta al animal para el peso objetivo de venta de su categoría.',
      narration:
        'Y la última: Engorde. Una barra de progreso que compara el peso de hoy contra el objetivo de venta de su categoría. La barra llena significa que el animal ya está en peso de salir: a venderlo antes de que se pase. La meta la podés ajustar, y ahorita te muestro dónde.',
    },
    {
      id: 'col-personalizar',
      route: '/animals',
      text: 'La app RECUERDA tu selección de columnas. Destildá las que no uses a diario y dejá sólo las que sí: la tabla queda a tu medida.',
      narration:
        'Un último secreto de esta pantalla: la app se acuerda de las columnas que elegiste, y las próximas veces ya las trae así. Así que ahora que las probaste todas, quedate sólo con las que de verdad mirás todos los días, y destildá las demás: la tabla queda cortita y a tu medida, como el chaleco del almacenero.',
    },
    {
      id: 'ojito-detalle',
      route: '/animals',
      target: '[data-ar-guide="animals-row-ver"]',
      label: 'El ojito de la fila',
      text: 'Ahora abrí la ficha completa: tocá el OJITO que está en la fila del 999, al lado de la caravana.',
      narration:
        'Vamos a la ficha de nuestro ternero. En su fila, al lado de la caravana, tenés el ojito: tocálo y se abre la página completa del animal.',
    },
    {
      id: 'ficha-del-animal',
      text: 'Esta página es la FICHA del animal: sus datos, su historia y sus números — todo lo que la app sabe de él, en un solo lugar.',
      narration:
        'Mirala con calma: esta es la ficha del animal. Acá vive todo lo que la app sabe de él: sus datos, su madre y su padre si los cargaste, sus eventos, sus pesos. Cualquier duda sobre un bicho puntual, la respuesta está acá.',
    },
    {
      id: 'progreso-engorde-ficha',
      target: '[data-ar-guide="animal-gdp-card"]',
      label: 'Progreso de engorde (abajo)',
      advanceOnClick: false,
      text: 'Bajá hasta "Progreso de engorde" (abajo de la ficha): el peso actual contra el objetivo, la GDP —la del 999 tiene que decir 1 kg/día—, y hace cuánto no lo pesás.',
      narration:
        'Y allá abajo, la perla: la card de Progreso de engorde. El peso de hoy contra el objetivo de su categoría, la ganancia diaria que viene haciendo, y hace cuánto fue el último pesaje. Y mirá el nueve nueve nueve: ahí tiene que estar diciendo un kilo por día, calculado con los dos pesajes que le cargamos. Sin calculadora ni libretita.',
    },
    {
      id: 'evolucion-peso',
      target: '[data-ar-guide="animal-btn-evolucion"]',
      label: '📊 Evolución de Peso',
      advanceOnClick: false,
      text: 'Para ver los pesajes en detalle, tocá "📊 Evolución de Peso": la curva completa, pesaje a pesaje.',
      narration:
        '¿Querés ver los pesajes uno por uno, en curva? Tocá Evolución de Peso y ahí está la historia completa: cada pesaje con su fecha, la pendiente de la ganancia, y el comparativo. Es la radiografía del engorde.',
    },
    {
      id: 'curva-evolucion',
      text: '¡Acá están! Cada punto de la curva es un pesaje con su fecha — el 999 con sus 100 y sus 130 kilos, a un mes de distancia. La línea que los une ES la ganancia.',
      narration:
        '¿La ves? Cada punto de esa curva es un pesaje: los cien kilos de hace un mes, y los ciento treinta de hoy. La línea que los une es la ganancia, la pendiente cuenta la historia: empinada, el animal va lindo; plana, hay que revisar la olla. Cuando cargues más pesajes, la curva se va armando sola.',
    },
    {
      id: 'meta-peso-objetivo',
      route: '/configuraciones',
      target: '[data-ar-guide="config-tab-pesoObjetivo"]',
      label: 'Pestaña Peso Objetivo (Configuraciones)',
      text: '¿Querés cambiar la meta? El peso objetivo se define acá: Configuraciones → pestaña "Peso Objetivo". Tocála y poné a qué peso vendés cada categoría.',
      narration:
        'Y te dejo el último dato del módulo: la meta de engorde se cambia en Configuraciones, en la pestaña Peso Objetivo. Ahí definís a qué peso querés vender cada categoría, y con eso se arma la barra de Engorde que vimos. Tu negocio, tus números: la app se amolda.',
    },
    {
      id: 'cierre-pesajes',
      text: 'Con dos sesiones de pesaje ya tenés ganancia diaria; con la meta puesta, sabés cuánto falta. Pesá dos veces al año y el pulso del campo queda tomado. ¡Módulo completo!',
      narration:
        'Y ahí está el círculo cerrado, criollo: dos sesiones de pesaje, dos pesos y dos fechas, y la ganancia diaria sale sola; la meta de venta te dice cuánto falta; y las columnas de la lista te dejan vigilar todo el rodeo de un vistazo. Pesá dos veces al año, un rato de manga por vez, y el pulso completo del campo queda tomado. Lo que no se mide, no se mejora. ¡Nos vemos!',
    },
  ],
  completionCheck: async () => {
    // Un animal con peso inicial + peso actual = al menos dos pesajes registrados.
    const animales = await getRepositories().animal.getAllForActiveEmpresa();
    return animales.some(a => a.peso != null && a.peso_inicial != null);
  },
};
